import {
  FC,
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
} from 'react';
import { useSnackbar } from 'notistack';
import { defaultTo } from 'lodash';
import { TableChangeType } from 'react-bootstrap-table-next';
import { compact } from 'lodash';

import {
  useOrdersSearchFilters,
  useLazyRequest,
  useRequest,
} from '~hooks/index';
import {
  OrdersListAutocompleteMultipleRef,
  OrdersListAutocompleteSingleRef,
} from '~hooks/useOrdersListFilters';
import { searchItems } from '~services/item';
import {
  ItemFilter,
  ItemExtended,
  ItemsSearchHead,
} from '~services/item/types';
import { getDriverCompanyAvailables } from '~services/driver';
import { DriverCompanyBase } from '~services/driver/types';
import { getVehicles } from '~services/vehicle';
import { VehicleBase } from '~services/vehicle/types';
import { getServiceTypesCompany } from '~services/enumerations';
import { getLogistiscZonesInfo } from '~services/logisticsZones';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { getUsersWithDrivers } from '~services/route';
import { RouteUserBasic } from '~services/route/types';
import {
  DataPaginateResponse,
  PaginationRequestParams,
  SelectOption,
} from '~globals/types';
import { sortOrderPendingByExpirationDate } from '~utils/order';
import { sortAlphabeticZoneByCode } from '~utils/logisiticsZones';

import { DialogOrderSearchContextValue } from './types';
import {
  DEFAULT_DRIVER_OPTION,
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_STATE,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_SERVICE_TYPE_OPTION,
  DEFAULT_ZONE_OPTION,
} from './utils';

const DialogOrderSearchContext = createContext<DialogOrderSearchContextValue>({
  selectsData: {
    drivers: {
      autocompleteRef: { current: null },
      loading: true,
      list: [],
    },
    vehicles: {
      autocompleteRef: { current: null },
      loading: true,
      list: [],
    },
    services: {
      autocompleteRef: { current: null },
      loading: true,
      list: [],
    },
    logisticsZones: {
      autocompleteRef: { current: null },
      loading: true,
      list: [],
    },
    users: {
      autocompleteRef: { current: null },
      loading: true,
      list: [],
    },
    states: {
      autocompleteRef: { current: null },
    },
  },
  filters: {
    isFilterApplied: false,
    setIsFilterApplied: () => {},
    filters: DEFAULT_FILTERS_STATE,
    setFilterByKey: () => {},
    handleChangeInputFilter: () => {},
    clearFilters: () => {},
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    filtersActives: DEFAULT_FILTERS_STATE,
    setFiltersActives: () => {},
    setFilterActiveByKey: () => {},
    clearFiltersActives: () => {},
  },
  table: {
    data: [],
    loading: true,
    totalSize: 0,
    pagination: DEFAULT_PAGINATION,
    handleChangeTable: () => {},
    handleInitialTable: () => {},
  },
});

export const useDialogOrderSearchContext = (): DialogOrderSearchContextValue =>
  useContext(DialogOrderSearchContext);

const DialogOrderSearchProvider: FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const driversAutocompleteRef =
    useRef<OrdersListAutocompleteMultipleRef<DriverCompanyBase>>(null);
  const [drivers, setDrivers] = useState<DriverCompanyBase[]>([]);
  const [, loadingDrivers] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    withPostSuccess: (response) => {
      const availablesDriversResponse = response.data?.data
        .results as DriverCompanyBase[];

      setDrivers([DEFAULT_DRIVER_OPTION, ...availablesDriversResponse]);
    },
  });

  const vehiclesAutocompleteRef =
    useRef<OrdersListAutocompleteMultipleRef<VehicleBase>>(null);
  const [vehicles, setVehicles] = useState<VehicleBase[]>([]);
  const [, loadingVehicles] = useRequest({
    request: getVehicles,
    payload: null,
    withPostSuccess: (response) => {
      const vehiclesResponse = response.data?.data as VehicleBase[];

      setVehicles(vehiclesResponse);
    },
  });

  const servicesAutocompleteRef =
    useRef<OrdersListAutocompleteMultipleRef<SelectOption>>(null);
  const [serviceTypes, setServicesTypes] = useState<SelectOption[]>([]);
  const [, loadingServiceTypes] = useRequest({
    request: getServiceTypesCompany,
    payload: null,
    withPostSuccess: (response) => {
      const servicesListResponse = compact(
        response.data?.data.results as string[],
      );

      const currentServiceTypes: SelectOption[] = servicesListResponse.map(
        (service) => ({
          value: service,
          label: service,
        }),
      );

      setServicesTypes([DEFAULT_SERVICE_TYPE_OPTION, ...currentServiceTypes]);
    },
  });

  const logiticsZonesAutocompleteRef =
    useRef<OrdersListAutocompleteMultipleRef<LogisticZoneBase>>(null);
  const [logisticsZones, setLogisticsZone] = useState<LogisticZoneBase[]>([]);
  const [, loadingLogisticsZone] = useRequest({
    request: getLogistiscZonesInfo,
    payload: null,
    withPostSuccess: (response) => {
      const logisitcisZoneResponse = response.data?.data
        .results as LogisticZoneBase[];

      const sortedLogisitcisZoneResponse = sortAlphabeticZoneByCode(
        logisitcisZoneResponse,
      );

      setLogisticsZone([DEFAULT_ZONE_OPTION, ...sortedLogisitcisZoneResponse]);
    },
  });

  const usersAutocompleteRef =
    useRef<OrdersListAutocompleteSingleRef<RouteUserBasic>>(null);
  const [users, setUsers] = useState<RouteUserBasic[]>([]);
  const [, loadingUsers] = useRequest({
    request: getUsersWithDrivers,
    payload: null,
    withPostSuccess: (response) => {
      const currentUsersResponse = response.data?.data
        .results as RouteUserBasic[];

      setUsers(currentUsersResponse);
    },
  });

  const statesAutocompleteRef =
    useRef<OrdersListAutocompleteMultipleRef<SelectOption>>(null);

  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

  const filters = useOrdersSearchFilters({
    initialFilters: DEFAULT_FILTERS_STATE,
  });

  const [tableData, setTableData] = useState<ItemExtended[]>([]);
  const [tableTotalSize, setTableTotalSize] = useState<number>(0);
  const [tablePagination, setTablePagination] =
    useState<PaginationRequestParams<unknown>['pagination']>(
      DEFAULT_PAGINATION,
    );

  const [, loadingTable, , executeGetTableData] = useLazyRequest({
    request: searchItems,
    transformResponse: (res) => {
      const response = res.data as DataPaginateResponse<
        ItemExtended[],
        null,
        ItemsSearchHead
      >;

      if (response.data.results) {
        sortOrderPendingByExpirationDate(response.data.results);
      }

      return response;
    },
    withPostSuccess: (response) => {
      const {
        data: { pagination, results },
      } = response as unknown as DataPaginateResponse<ItemExtended[]>;

      setTableData(results);
      setTableTotalSize(defaultTo(pagination?.count, results.length));
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
    },
  });

  const handleInitialTable = useCallback(() => {
    setTableData([]);
    setTableTotalSize(0);
    setTablePagination(DEFAULT_PAGINATION);
  }, []);

  const handleChangeTable = useCallback(
    (_type: TableChangeType, newState: PaginationRequestParams<ItemFilter>) => {
      const currentPagination: PaginationRequestParams<unknown>['pagination'] =
        {
          ...tablePagination,
          ...newState.pagination,
          page:
            (newState.pagination?.page as number) === 0
              ? 1
              : newState.pagination?.page,
        };

      setTablePagination(currentPagination);

      executeGetTableData({
        pagination: currentPagination,
        filters: newState.filters,
      });
    },
    [executeGetTableData, tablePagination],
  );

  return (
    <DialogOrderSearchContext.Provider
      value={{
        selectsData: {
          drivers: {
            autocompleteRef: driversAutocompleteRef,
            loading: loadingDrivers,
            list: drivers,
          },
          vehicles: {
            autocompleteRef: vehiclesAutocompleteRef,
            loading: loadingVehicles,
            list: vehicles,
          },
          services: {
            autocompleteRef: servicesAutocompleteRef,
            loading: loadingServiceTypes,
            list: serviceTypes,
          },
          logisticsZones: {
            autocompleteRef: logiticsZonesAutocompleteRef,
            loading: loadingLogisticsZone,
            list: logisticsZones,
          },
          users: {
            autocompleteRef: usersAutocompleteRef,
            loading: loadingUsers,
            list: users,
          },
          states: {
            autocompleteRef: statesAutocompleteRef,
          },
        },
        filters: {
          ...filters,
          isFilterApplied,
          setIsFilterApplied,
        },
        table: {
          data: tableData,
          loading: loadingTable,
          totalSize: tableTotalSize,
          pagination: tablePagination,
          handleChangeTable,
          handleInitialTable,
        },
      }}
    >
      {children}
    </DialogOrderSearchContext.Provider>
  );
};

export default DialogOrderSearchProvider;
